import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  ClipboardList,
  Plus,
  Camera,
  PenLine,
  MapPin,
  Wrench,
  Star,
  BarChart2,
  Loader2,
  ChevronDown,
  ChevronRight,
  Copy,
  Trash2,
} from 'lucide-react';

interface TemplateItem {
  label: string;
  required: boolean;
  allow_photo: boolean;
  fail_creates_work_order: boolean;
}

interface TemplateCategory {
  name: string;
  items: TemplateItem[];
}

interface InspectionTemplate {
  id: string;
  name: string;
  description?: string;
  vehicle_types?: string[];
  categories: TemplateCategory[];
  require_photos: boolean;
  require_signature: boolean;
  require_gps: boolean;
  auto_create_work_order: boolean;
  is_default: boolean;
  created_at: string;
}

interface TemplateAnalytics {
  by_template: { name: string; total: number; passed: number; failed: number }[];
  by_category: { template_name: string; category: string; item_count: number }[];
  total_inspections: number;
  pass_rate: number;
}

const MOCK_TEMPLATES: InspectionTemplate[] = [
  {
    id: '1',
    name: 'Pre-Trip DOT Standard',
    description: 'Federal DOT pre-trip inspection checklist',
    vehicle_types: ['truck', 'van'],
    categories: [
      {
        name: 'Engine & Fluids',
        items: [
          { label: 'Engine oil level', required: true, allow_photo: false, fail_creates_work_order: true },
          { label: 'Coolant level', required: true, allow_photo: false, fail_creates_work_order: true },
          { label: 'Brake fluid', required: true, allow_photo: true, fail_creates_work_order: true },
        ],
      },
      {
        name: 'Tires & Brakes',
        items: [
          { label: 'Tire pressure (all wheels)', required: true, allow_photo: true, fail_creates_work_order: true },
          { label: 'Tread depth', required: true, allow_photo: false, fail_creates_work_order: false },
          { label: 'Brake pad thickness', required: true, allow_photo: false, fail_creates_work_order: true },
        ],
      },
    ],
    require_photos: true,
    require_signature: true,
    require_gps: true,
    auto_create_work_order: true,
    is_default: true,
    created_at: new Date().toISOString(),
  },
  {
    id: '2',
    name: 'Post-Trip Walkthrough',
    description: 'End-of-shift vehicle check',
    vehicle_types: ['car', 'van', 'suv'],
    categories: [
      {
        name: 'Exterior',
        items: [
          { label: 'Body damage', required: true, allow_photo: true, fail_creates_work_order: false },
          { label: 'Lights working', required: true, allow_photo: false, fail_creates_work_order: false },
        ],
      },
    ],
    require_photos: false,
    require_signature: true,
    require_gps: false,
    auto_create_work_order: false,
    is_default: false,
    created_at: new Date().toISOString(),
  },
];

const MOCK_ANALYTICS: TemplateAnalytics = {
  by_template: [
    { name: 'Pre-Trip DOT Standard', total: 142, passed: 121, failed: 21 },
    { name: 'Post-Trip Walkthrough', total: 89, passed: 85, failed: 4 },
  ],
  by_category: [],
  total_inspections: 231,
  pass_rate: 89,
};

function passRate(total: number, passed: number): number {
  if (total === 0) return 0;
  return Math.round((passed / total) * 100);
}

function OptionBadge({ active, icon: Icon, label }: { active: boolean; icon: React.ComponentType<{ className?: string }>; label: string }) {
  if (!active) return null;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

export default function InspectionTemplatesPage() {
  const { data: templates, isLoading } = useApi<InspectionTemplate[]>('/api/inspection-templates');
  const { data: analytics } = useApi<TemplateAnalytics>('/api/inspection-templates/analytics');
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(false);

  // New template form state
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const templateList = templates ?? MOCK_TEMPLATES;
  const an = analytics ?? MOCK_ANALYTICS;

  const totalItems = templateList.reduce(
    (sum, t) => sum + t.categories.reduce((s, c) => s + c.items.length, 0),
    0,
  );

  const metricCards = [
    { label: 'Total Templates', value: templateList.length, icon: ClipboardList, color: 'text-blue-400' },
    { label: 'Active Inspections', value: an.total_inspections, icon: BarChart2, color: 'text-emerald-400' },
    { label: 'Pass Rate %', value: an.pass_rate + '%', icon: ClipboardList, color: 'text-emerald-400' },
    { label: 'Total Items', value: totalItems, icon: Wrench, color: 'text-zinc-400' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10">
            <ClipboardList className="w-6 h-6 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Inspection Templates</h1>
            <p className="text-sm text-zinc-400">Mobile inspection checklists and analytics</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-medium rounded-lg transition-colors"
          >
            <BarChart2 className="w-4 h-4" />
            Analytics
          </button>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Template
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricCards.map((m) => (
          <div key={m.label} className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-zinc-400 uppercase tracking-wide">{m.label}</span>
              <m.icon className={`w-4 h-4 ${m.color}`} />
            </div>
            <span className="text-2xl font-bold text-zinc-100">{m.value}</span>
          </div>
        ))}
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-zinc-200 mb-4">New Inspection Template</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Template Name</label>
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Pre-Trip DOT Standard"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Description</label>
              <input
                type="text"
                value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
                placeholder="Optional description"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>
          <p className="text-xs text-zinc-500 mb-4">Add categories and items after creating the template.</p>
          <div className="flex gap-3">
            <button className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors">
              Create Template
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Templates List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
        </div>
      ) : templateList.length === 0 ? (
        <div className="text-center py-16 text-zinc-500 bg-zinc-900/50 border border-zinc-800 rounded-xl">
          <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>No inspection templates</p>
        </div>
      ) : (
        <div className="space-y-3">
          {templateList.map((tmpl) => {
            const totalItemCount = tmpl.categories.reduce((s, c) => s + c.items.length, 0);
            const isExpanded = expanded === tmpl.id;

            return (
              <div key={tmpl.id} className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl overflow-hidden">
                {/* Template Header Row */}
                <div
                  className="flex items-center gap-3 px-4 py-4 cursor-pointer hover:bg-zinc-800/30 transition-colors"
                  onClick={() => setExpanded(isExpanded ? null : tmpl.id)}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-zinc-100">{tmpl.name}</span>
                      {tmpl.is_default && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                          <Star className="w-3 h-3" />
                          Default
                        </span>
                      )}
                    </div>
                    {tmpl.description && (
                      <p className="text-xs text-zinc-500 mt-0.5">{tmpl.description}</p>
                    )}
                  </div>

                  {/* Vehicle types */}
                  {tmpl.vehicle_types && tmpl.vehicle_types.length > 0 && (
                    <div className="hidden sm:flex gap-1">
                      {tmpl.vehicle_types.map(vt => (
                        <span key={vt} className="px-1.5 py-0.5 text-xs rounded bg-zinc-800 text-zinc-400 border border-zinc-700 capitalize">
                          {vt}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Counts */}
                  <div className="flex items-center gap-4 text-xs text-zinc-500 flex-shrink-0">
                    <span>{tmpl.categories.length} categories</span>
                    <span>{totalItemCount} items</span>
                  </div>

                  {/* Option badges */}
                  <div className="hidden lg:flex gap-1 flex-shrink-0">
                    <OptionBadge active={tmpl.require_photos} icon={Camera} label="Photos" />
                    <OptionBadge active={tmpl.require_signature} icon={PenLine} label="Signature" />
                    <OptionBadge active={tmpl.require_gps} icon={MapPin} label="GPS" />
                    <OptionBadge active={tmpl.auto_create_work_order} icon={Wrench} label="Auto-WO" />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <button className="p-1.5 rounded hover:bg-zinc-700 text-zinc-500 hover:text-zinc-300 transition-colors" title="Clone">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button className="p-1.5 rounded hover:bg-red-500/10 text-zinc-500 hover:text-red-400 transition-colors" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Expanded Category Detail */}
                {isExpanded && (
                  <div className="border-t border-zinc-800 px-4 py-4 space-y-4">
                    {tmpl.categories.map((cat) => (
                      <div key={cat.name}>
                        <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wide mb-2">{cat.name}</h3>
                        <div className="space-y-1">
                          {cat.items.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-3 py-1.5 px-3 rounded-lg bg-zinc-800/50">
                              <span className="flex-1 text-sm text-zinc-300">{item.label}</span>
                              <div className="flex gap-1">
                                {item.required && (
                                  <span className="px-1.5 py-0.5 text-xs rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">Required</span>
                                )}
                                {item.allow_photo && (
                                  <span className="px-1.5 py-0.5 text-xs rounded bg-zinc-700 text-zinc-400">Photo</span>
                                )}
                                {item.fail_creates_work_order && (
                                  <span className="px-1.5 py-0.5 text-xs rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">Auto-WO</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Analytics Section */}
      {showAnalytics && (
        <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-emerald-400" />
            <h2 className="text-sm font-semibold text-zinc-200">Pass/Fail Analytics (Last 90 Days)</h2>
          </div>
          {an.by_template.length === 0 ? (
            <div className="text-center py-8 text-zinc-500 text-sm">No inspection data available</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Template</th>
                    <th className="text-right text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Total</th>
                    <th className="text-right text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Passed</th>
                    <th className="text-right text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Failed</th>
                    <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3 min-w-[140px]">Pass Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/50">
                  {an.by_template.map((row) => {
                    const rate = passRate(row.total, row.passed);
                    return (
                      <tr key={row.name} className="hover:bg-zinc-800/30 transition-colors">
                        <td className="px-4 py-3 text-sm font-medium text-zinc-100">{row.name}</td>
                        <td className="px-4 py-3 text-sm text-zinc-400 text-right">{row.total}</td>
                        <td className="px-4 py-3 text-sm text-emerald-400 text-right font-medium">{row.passed}</td>
                        <td className="px-4 py-3 text-sm text-red-400 text-right font-medium">{row.failed}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-zinc-800 rounded-full h-1.5 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${rate >= 90 ? 'bg-emerald-500' : rate >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
                                style={{ width: `${rate}%` }}
                              />
                            </div>
                            <span className="text-xs font-mono text-zinc-400 w-8 text-right">{rate}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
